import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export const useSimpleExitModal = (hasUnsavedChanges = false) => {
  const originalNavigate = useNavigate();
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [bypassNextNavigation, setBypassNextNavigation] = useState(false);

  // Protected navigate function
  const navigate = useCallback((to, options = {}) => {
    if (hasUnsavedChanges && !bypassNextNavigation) {
      setPendingNavigation({ to, options });
      setShowExitModal(true);
    } else {
      setBypassNextNavigation(false); // Reset flag
      originalNavigate(to, options);
    }
  }, [hasUnsavedChanges, bypassNextNavigation, originalNavigate]);

  // Force navigate without showing modal (for success cases)
  const forceNavigate = useCallback((to, options = {}) => {
    setBypassNextNavigation(true);
    originalNavigate(to, options);
  }, [originalNavigate]);

  // Block browser back button and intercept all navigate calls
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.pathname);
      setPendingNavigation('back');
      setShowExitModal(true);
    };

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };

    // Intercept MainLayout back button clicks
    const handleDocumentClick = (event) => {
      const backButton = event.target.closest('.main-layout-back-button, [data-testid="main-layout-back-button"]');
      if (backButton) {
        event.preventDefault();
        event.stopPropagation();
        const navigationTarget = backButton.getAttribute('data-navigation-target') || 'back';
        setPendingNavigation(navigationTarget === 'back' ? 'back' : { to: navigationTarget });
        setShowExitModal(true);
      }
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [hasUnsavedChanges]);

  // Handle exit confirmation
  const handleConfirmExit = useCallback(() => {
    setShowExitModal(false);
    
    if (pendingNavigation === 'back') {
      originalNavigate(-1);
    } else if (pendingNavigation) {
      originalNavigate(pendingNavigation.to, pendingNavigation.options);
    }
    
    setPendingNavigation(null);
  }, [originalNavigate, pendingNavigation]);

  // Handle exit cancellation
  const handleCancelExit = useCallback(() => {
    setShowExitModal(false);
    setPendingNavigation(null);
  }, []);

  return {
    navigate,
    forceNavigate,
    showExitModal,
    handleConfirmExit,
    handleCancelExit
  };
};